import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconArchive,
  IconBuildingBank,
  IconChartBar,
  IconClockDollar,
  IconCloudUpload,
  IconFileInvoice,
  IconReceipt,
  IconReportMoney,
  IconSettings,
  IconX,
} from "@tabler/icons-react";
import {
  useAdminOrganizationsQuery,
  useAdminUsersQuery,
  useCreateOrganizationMutation,
  useDeleteOrgMicrosoftOAuthMutation,
  useInitZohoTokenMutation,
  useOrgMicrosoftOAuthQuery,
  useUpdateOrgMicrosoftOAuthMutation,
  useUpdateOrganizationMutation,
  useUpdateUserMutation,
  useZohoAuthorizeUrlMutation,
} from "../api/queries";
import {
  AdminOrganization,
  AdminUser,
  ALL_PAGE_SCOPES,
  PageScope,
  UserRole,
} from "../utils/types";
import { formatAppDateTime, INVALID_FALLBACK, parseApiDateTime } from "../utils/date";
import { TrialScope } from "./auth/TrialScope";
import { AdminScope } from "./auth/AdminScope";
import { AdminStatisticsPanel } from "./admin/AdminStatisticsPanel";
import { UserOrgLinkPicker } from "./admin/UserOrgLinkPicker";
import { toMs } from "./admin/adminStats";

type OrgFormState = {
  name: string;
  support_email: string;
  official_outgoing_email: string;
  use_official_outgoing_email: boolean;
  priority_api_url: string;
};
type AdminTab = "ORGANIZATIONS" | "USERS" | "STATISTICS" | "ZOHO";
type PendingChange = { field: string; from: string; to: string };

const defaultOrgForm: OrgFormState = {
  name: "",
  support_email: "",
  official_outgoing_email: "",
  use_official_outgoing_email: true,
  priority_api_url: "",
};

const roleOptions: UserRole[] = ["ADMIN", "TRIAL", "USER"];
const ASSIGNABLE_PAGE_SCOPES: PageScope[] = ALL_PAGE_SCOPES.filter(
  (scope) => scope !== "ADMIN",
);
const PAGE_SCOPE_META: Record<
  PageScope,
  {
    label: string;
    Icon: React.ComponentType<{ size?: number; className?: string }>;
  }
> = {
  CASHFLOW: { label: "Cashflow", Icon: IconReportMoney },
  FORECAST: { label: "Forecast", Icon: IconChartBar },
  TRANSACTIONS: { label: "Transactions", Icon: IconReceipt },
  DASHBOARDS: { label: "Dashboards", Icon: IconChartBar },
  RECEIVABLES: { label: "Receivables", Icon: IconClockDollar },
  SUPPLIER_INVOICES: {
    label: "Supplier Invoices",
    Icon: IconFileInvoice,
  },
  BANK_RECONCILIATION: {
    label: "Bank Reconciliation",
    Icon: IconBuildingBank,
  },
  FILES_INTEGRATIONS: { label: "Files & Integrations", Icon: IconCloudUpload },
  SETTINGS: { label: "Settings", Icon: IconSettings },
  ARCHIVE: { label: "Archive", Icon: IconArchive },
  ANALYTICS: { label: "Analytics", Icon: IconChartBar },
  ADMIN: { label: "Admin", Icon: IconSettings },
};
const ADMIN_TABS: Array<{ id: AdminTab; label: string }> = [
  { id: "USERS", label: "Users" },
  { id: "ORGANIZATIONS", label: "Organizations" },
  { id: "STATISTICS", label: "Statistics" },
  { id: "ZOHO", label: "Zoho" },
];

const ChangeConfirmationModal = ({
  isOpen,
  title,
  description,
  changes,
  onCancel,
  onConfirm,
  confirmLabel,
  isSubmitting,
}: {
  isOpen: boolean;
  title: string;
  description: string;
  changes: PendingChange[];
  onCancel: () => void;
  onConfirm: () => void;
  confirmLabel: string;
  isSubmitting: boolean;
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-200 flex items-center justify-center bg-slate-900/50 p-4">
      <div className="w-full max-w-2xl rounded-2xl border border-slate-200 bg-white p-5 shadow-xl">
        <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
        <p className="mt-1 text-sm text-slate-600">{description}</p>
        <div className="mt-4 max-h-[52vh] overflow-auto rounded-xl border border-slate-200">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="sticky top-0 bg-slate-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-slate-700">
                  Field
                </th>
                <th className="px-3 py-2 text-left font-medium text-slate-700">
                  Current
                </th>
                <th className="px-3 py-2 text-left font-medium text-slate-700">
                  New
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {changes.map((change) => (
                <tr key={change.field}>
                  <td className="px-3 py-2 font-medium text-slate-900">
                    {change.field}
                  </td>
                  <td className="px-3 py-2 text-slate-600">{change.from}</td>
                  <td className="px-3 py-2 text-slate-900">{change.to}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isSubmitting}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
          >
            {isSubmitting ? "Saving..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

type MicrosoftOAuthFormState = {
  tenant_id: string;
  client_id: string;
  client_secret: string;
  redirect_uri: string;
};

const MicrosoftOAuthOrgSection = ({ orgId }: { orgId: number }) => {
  const { data, isLoading } = useOrgMicrosoftOAuthQuery(orgId);
  const updateConfig = useUpdateOrgMicrosoftOAuthMutation();
  const deleteConfig = useDeleteOrgMicrosoftOAuthMutation();
  const [formState, setFormState] = useState<MicrosoftOAuthFormState>({
    tenant_id: "common",
    client_id: "",
    client_secret: "",
    redirect_uri: "",
  });
  const [pendingChanges, setPendingChanges] = useState<PendingChange[] | null>(
    null,
  );
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    setFormState({
      tenant_id: data.tenant_id || "common",
      client_id: data.client_id || "",
      client_secret: "",
      redirect_uri: data.redirect_uri || "",
    });
  }, [data]);

  const handleChange = (
    field: keyof MicrosoftOAuthFormState,
    value: string,
  ) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    if (saveError) setSaveError(null);
  };

  const buildMicrosoftChanges = (): PendingChange[] => {
    if (!data) return [];

    if (!data.configured) {
      const tenant = formState.tenant_id.trim();
      const clientId = formState.client_id.trim();
      const redirectUri = formState.redirect_uri.trim();
      const clientSecret = formState.client_secret.trim();
      if (!tenant || !clientId || !redirectUri || !clientSecret) {
        return [];
      }
      return [
        {
          field: "Organization Microsoft OAuth",
          from: "Environment fallback (not stored for this org)",
          to: "Save organization credentials",
        },
      ];
    }

    const changes: PendingChange[] = [];
    const upsert = (field: string, before: string, after: string) => {
      if (before !== after) changes.push({ field, from: before, to: after });
    };
    upsert("Tenant ID", data.tenant_id || "common", formState.tenant_id.trim());
    upsert(
      "Client ID",
      data.client_id || "(empty)",
      formState.client_id.trim(),
    );
    upsert(
      "Redirect URI",
      data.redirect_uri || "(empty)",
      formState.redirect_uri.trim(),
    );
    if (formState.client_secret.trim()) {
      changes.push({
        field: "Client Secret",
        from: data.has_client_secret ? "(unchanged)" : "(empty)",
        to: "(updated)",
      });
    }
    return changes;
  };

  const submitMicrosoftOAuthSave = () => {
    setSaveError(null);
    updateConfig.mutate(
      {
        orgId,
        tenant_id: formState.tenant_id.trim(),
        client_id: formState.client_id.trim(),
        redirect_uri: formState.redirect_uri.trim(),
        client_secret: formState.client_secret.trim() || undefined,
      },
      {
        onSuccess: () => {
          setFormState((prev) => ({ ...prev, client_secret: "" }));
        },
        onError: (error: unknown) => {
          const message =
            typeof error === "object" &&
            error !== null &&
            "response" in error &&
            typeof (
              error as {
                response?: { data?: { detail?: { message?: string } } };
              }
            ).response?.data?.detail === "object"
              ? (
                  error as {
                    response: { data: { detail: { message?: string } } };
                  }
                ).response.data.detail.message
              : "Failed to save Microsoft OAuth credentials.";
          setSaveError(
            message || "Failed to save Microsoft OAuth credentials.",
          );
        },
      },
    );
  };

  const handleSaveClick = () => {
    setSaveError(null);
    const changes = buildMicrosoftChanges();
    if (changes.length === 0) {
      if (!data?.configured) {
        setSaveError(
          "Enter tenant ID, client ID, redirect URI, and client secret to save organization credentials.",
        );
      }
      return;
    }
    if (!data?.configured) {
      submitMicrosoftOAuthSave();
      return;
    }
    setPendingChanges(changes);
  };

  const handleConfirmSubmit = () => {
    setPendingChanges(null);
    submitMicrosoftOAuthSave();
  };

  const handleClearOverride = () => {
    if (!data?.configured) return;
    deleteConfig.mutate(orgId);
  };

  const statusLabel =
    data?.configured === true
      ? "Using organization config"
      : data?.source === "env"
        ? "Using environment fallback"
        : "Not configured";

  return (
    <AdminScope>
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">
              Microsoft OAuth
            </h3>
            <p className="mt-0.5 text-xs text-slate-600">
              Per-organization app credentials for Microsoft login and Outlook.
              Changing tenant or client ID may require users to reconnect.
            </p>
          </div>
          <span
            className={`rounded-full px-2.5 py-1 text-xs font-medium ${
              data?.configured
                ? "bg-indigo-100 text-indigo-700"
                : "bg-amber-100 text-amber-800"
            }`}
          >
            {isLoading ? "Loading..." : statusLabel}
          </span>
        </div>

        {!data?.configured && !isLoading && (
          <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            Credentials are not stored for this organization yet. The fields
            below may be prefilled from environment settings, but you must enter
            the client secret and click Save to enable organization email login.
          </p>
        )}

        <div className="mt-4 flex flex-col gap-3">
          <label className="flex flex-col text-sm text-slate-700">
            Tenant ID
            <input
              className="mt-1 rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
              value={formState.tenant_id}
              onChange={(e) => handleChange("tenant_id", e.target.value)}
              placeholder="common, consumers, organizations, or your Azure directory GUID"
            />
          </label>
          <label className="flex flex-col text-sm text-slate-700">
            Client ID
            <input
              className="mt-1 rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
              value={formState.client_id}
              onChange={(e) => handleChange("client_id", e.target.value)}
            />
          </label>
          <label className="flex flex-col text-sm text-slate-700">
            Client Secret
            <input
              type="password"
              autoComplete="new-password"
              className="mt-1 rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
              value={formState.client_secret}
              onChange={(e) => handleChange("client_secret", e.target.value)}
              placeholder={
                data?.configured && data?.has_client_secret
                  ? "Leave blank to keep existing secret"
                  : "Required on first save"
              }
            />
          </label>
          <label className="flex flex-col text-sm text-slate-700">
            Redirect URI
            <input
              className="mt-1 rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
              value={formState.redirect_uri}
              onChange={(e) => handleChange("redirect_uri", e.target.value)}
              placeholder="https://app.example.com/microsoft_oauth_redirect"
            />
          </label>
          {saveError && (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {saveError}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleSaveClick}
              disabled={updateConfig.isPending || isLoading}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
            >
              {updateConfig.isPending ? "Saving..." : "Save Microsoft OAuth"}
            </button>
            {data?.configured && (
              <button
                type="button"
                onClick={handleClearOverride}
                disabled={deleteConfig.isPending}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {deleteConfig.isPending ? "Clearing..." : "Use env fallback"}
              </button>
            )}
          </div>
        </div>
      </div>
      <ChangeConfirmationModal
        isOpen={pendingChanges !== null}
        title="Confirm Microsoft OAuth updates"
        description="Review credential changes before saving."
        changes={pendingChanges ?? []}
        onCancel={() => setPendingChanges(null)}
        onConfirm={handleConfirmSubmit}
        confirmLabel="Save Microsoft OAuth"
        isSubmitting={updateConfig.isPending}
      />
    </AdminScope>
  );
};

const OrganizationsPanel = ({
  organizations,
  onSubmit,
  onSelect,
  selectedOrg,
  isSubmitting,
}: {
  organizations: AdminOrganization[];
  onSubmit: (data: OrgFormState, orgId?: number) => void;
  onSelect: (orgId: number | null) => void;
  selectedOrg: AdminOrganization | null;
  isSubmitting: boolean;
}) => {
  const [formState, setFormState] = useState<OrgFormState>(defaultOrgForm);
  const [orgEditorOpen, setOrgEditorOpen] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<PendingChange[] | null>(
    null,
  );

  const closeOrgEditor = useCallback(() => {
    setOrgEditorOpen(false);
    onSelect(null);
  }, [onSelect]);

  const formatText = (value: string | null | undefined) => {
    const normalized = (value ?? "").trim();
    return normalized.length > 0 ? normalized : "Not set";
  };

  const buildOrganizationChanges = (
    currentOrg: AdminOrganization | null,
    nextState: OrgFormState,
  ): PendingChange[] => {
    const fields: PendingChange[] = [];
    const upsert = (field: string, before: string, after: string) => {
      if (before !== after) {
        fields.push({ field, from: before, to: after });
      }
    };

    const nextName = formatText(nextState.name);
    const nextSupportEmail = formatText(nextState.support_email);
    const nextOutgoingEmail = formatText(nextState.official_outgoing_email);
    const nextPriorityUrl = formatText(nextState.priority_api_url);
    const nextUseOfficial = nextState.use_official_outgoing_email
      ? "Yes"
      : "No";

    if (!currentOrg) {
      return [
        { field: "Name", from: "(new)", to: nextName },
        { field: "Support Email", from: "(new)", to: nextSupportEmail },
        {
          field: "Official Outgoing Email",
          from: "(new)",
          to: nextOutgoingEmail,
        },
        { field: "Priority API URL", from: "(new)", to: nextPriorityUrl },
        {
          field: "Use Official Outgoing Email",
          from: "(new)",
          to: nextUseOfficial,
        },
      ];
    }

    upsert("Name", formatText(currentOrg.name), nextName);
    upsert(
      "Support Email",
      formatText(currentOrg.support_email),
      nextSupportEmail,
    );
    upsert(
      "Official Outgoing Email",
      formatText(currentOrg.official_outgoing_email),
      nextOutgoingEmail,
    );
    upsert(
      "Priority API URL",
      formatText(currentOrg.priority_api_url),
      nextPriorityUrl,
    );
    upsert(
      "Use Official Outgoing Email",
      currentOrg.use_official_outgoing_email ? "Yes" : "No",
      nextUseOfficial,
    );

    return fields;
  };

  useEffect(() => {
    if (selectedOrg) {
      setFormState({
        name: selectedOrg.name,
        support_email: selectedOrg.support_email,
        official_outgoing_email: selectedOrg.official_outgoing_email || "",
        use_official_outgoing_email: selectedOrg.use_official_outgoing_email,
        priority_api_url: selectedOrg.priority_api_url || "",
      });
    } else {
      setFormState(defaultOrgForm);
    }
  }, [selectedOrg]);

  const handleChange = (field: keyof OrgFormState, value: string | boolean) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const changes = buildOrganizationChanges(selectedOrg, formState);
    if (changes.length === 0) return;
    setPendingChanges(changes);
  };

  const handleConfirmSubmit = () => {
    if (!pendingChanges) return;
    setPendingChanges(null);
    onSubmit(formState, selectedOrg?.id);
  };

  useEffect(() => {
    if (!orgEditorOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [orgEditorOpen]);

  useEffect(() => {
    if (!orgEditorOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeOrgEditor();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [orgEditorOpen, closeOrgEditor]);

  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            Organizations
          </h2>
          <p className="text-sm text-slate-600">
            View, create, or update organizations.
          </p>
        </div>
        <button
          type="button"
          className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-sm font-medium text-indigo-700 transition hover:bg-indigo-100"
          onClick={() => {
            onSelect(null);
            setOrgEditorOpen(true);
          }}
        >
          + New
        </button>
      </div>

      <div className="max-h-[min(52vh,480px)] overflow-auto rounded-xl border border-slate-100">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 sticky top-0 z-10">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-slate-700">
                Name
              </th>
              <th className="px-4 py-2 text-left font-medium text-slate-700">
                Support Email
              </th>
              <th className="px-4 py-2 text-left font-medium text-slate-700">
                Sending Mode
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {organizations.map((org) => (
              <tr
                key={org.id}
                className={`cursor-pointer hover:bg-gray-50 ${
                  selectedOrg?.id === org.id ? "bg-indigo-50" : ""
                }`}
                onClick={() => {
                  onSelect(org.id);
                  setOrgEditorOpen(true);
                }}
              >
                <td className="px-4 py-2 text-slate-900 font-medium">
                  {org.name}
                </td>
                <td className="px-4 py-2 text-slate-700">
                  {org.support_email}
                </td>
                <td className="px-4 py-2 text-slate-600">
                  {org.auto_email_sending_mode}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {organizations.length === 0 && (
          <div className="p-4 text-center text-sm text-slate-500">
            No organizations found.
          </div>
        )}
      </div>

      {orgEditorOpen && (
        <>
          <div
            role="presentation"
            className="fixed inset-0 z-110 bg-slate-900/40"
            onClick={closeOrgEditor}
          />
          <aside
            className="fixed inset-y-0 right-0 z-120 flex w-full max-w-lg flex-col border-l border-slate-200 bg-white shadow-2xl"
            aria-labelledby="org-edit-title"
            aria-modal="true"
            role="dialog"
          >
            <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
              <div className="min-w-0">
                <p
                  id="org-edit-title"
                  className="truncate text-lg font-semibold text-slate-900"
                >
                  {selectedOrg
                    ? `Update ${selectedOrg.name}`
                    : "New organization"}
                </p>
                <p className="mt-0.5 text-sm text-slate-500">
                  {selectedOrg
                    ? `${selectedOrg.support_email} · ${selectedOrg.auto_email_sending_mode}`
                    : "Create a new organization record."}
                </p>
              </div>
              <button
                type="button"
                onClick={closeOrgEditor}
                className="shrink-0 rounded-lg border border-slate-200 p-2 text-slate-600 transition hover:bg-slate-50"
                aria-label="Close"
              >
                <IconX size={20} stroke={1.75} />
              </button>
            </div>

            <form
              className="flex min-h-0 flex-1 flex-col"
              onSubmit={handleSubmit}
            >
              <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-5 py-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <label className="flex flex-col text-sm text-slate-700">
                    Name
                    <input
                      className="mt-1 rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                      required
                      value={formState.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                    />
                  </label>
                  <label className="flex flex-col text-sm text-slate-700">
                    Support Email
                    <input
                      className="mt-1 rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                      required
                      value={formState.support_email}
                      onChange={(e) =>
                        handleChange("support_email", e.target.value)
                      }
                    />
                  </label>
                  <label className="flex flex-col text-sm text-slate-700 md:col-span-2">
                    Official Outgoing Email
                    <input
                      className="mt-1 rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                      value={formState.official_outgoing_email}
                      onChange={(e) =>
                        handleChange("official_outgoing_email", e.target.value)
                      }
                    />
                  </label>
                  <label className="flex flex-col text-sm text-slate-700 md:col-span-2">
                    Priority API URL
                    <input
                      className="mt-1 rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                      value={formState.priority_api_url}
                      onChange={(e) =>
                        handleChange("priority_api_url", e.target.value)
                      }
                    />
                  </label>
                </div>

                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={formState.use_official_outgoing_email}
                    onChange={(e) =>
                      handleChange(
                        "use_official_outgoing_email",
                        e.target.checked,
                      )
                    }
                  />
                  Use official outgoing email
                </label>

                {selectedOrg && (
                  <MicrosoftOAuthOrgSection orgId={selectedOrg.id} />
                )}
              </div>

              <div className="flex flex-wrap gap-2 border-t border-slate-200 px-5 py-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
                >
                  {selectedOrg ? "Update organization" : "Create organization"}
                </button>
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  onClick={closeOrgEditor}
                >
                  Cancel
                </button>
              </div>
            </form>
          </aside>
        </>
      )}
      <ChangeConfirmationModal
        isOpen={pendingChanges !== null}
        title={
          selectedOrg
            ? "Confirm organization updates"
            : "Confirm new organization"
        }
        description="Review the changes before saving."
        changes={pendingChanges ?? []}
        onCancel={() => setPendingChanges(null)}
        onConfirm={handleConfirmSubmit}
        confirmLabel={selectedOrg ? "Save changes" : "Create organization"}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

const UsersPanel = ({
  users,
  organizations,
  onUpdateUser,
  isSubmitting,
}: {
  users: AdminUser[];
  organizations: AdminOrganization[];
  onUpdateUser: (
    userId: number,
    role: UserRole,
    organizationIds: number[],
    pageScopes: PageScope[],
  ) => void;
  isSubmitting: boolean;
}) => {
  const [userSearch, setUserSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedRole, setSelectedRole] = useState<UserRole>("USER");
  const [selectedOrgIds, setSelectedOrgIds] = useState<number[]>([]);
  const [selectedPageScopes, setSelectedPageScopes] = useState<PageScope[]>([
    "CASHFLOW",
  ]);
  const [pendingChanges, setPendingChanges] = useState<PendingChange[] | null>(
    null,
  );

  const resolveOrgNames = (orgIds: number[]) => {
    if (orgIds.length === 0) return "Unlinked";
    return orgIds
      .map((orgId) => {
        const org = organizations.find((item) => item.id === orgId);
        return org ? org.name : String(orgId);
      })
      .join(", ");
  };

  const getUserOrgIds = (user: AdminUser) =>
    user.budy_organization_ids ??
    (user.budy_organization_id !== null ? [user.budy_organization_id] : []);

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    const matched = !q
      ? users
      : users.filter((user) => {
          const orgName = resolveOrgNames(getUserOrgIds(user)).toLowerCase();
          const haystack = [
            user.username,
            user.email,
            String(user.id),
            user.roles,
            user.provider_user_id,
            orgName,
          ]
            .join(" ")
            .toLowerCase();
          return haystack.includes(q);
        });
    return [...matched].sort(
      (a, b) => (toMs(b.created_at) ?? 0) - (toMs(a.created_at) ?? 0),
    );
  }, [users, userSearch, organizations]);

  const selectedUser = useMemo(
    () => users.find((u) => u.id === selectedUserId) || null,
    [selectedUserId, users],
  );

  const closeUserEditor = useCallback(() => {
    setSelectedOrgIds([]);
    setSelectedRole("USER");
    setSelectedPageScopes(["CASHFLOW"]);
    setSelectedUserId(null);
  }, []);

  useEffect(() => {
    if (!selectedUser) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [selectedUser]);

  useEffect(() => {
    if (!selectedUser) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeUserEditor();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedUser, closeUserEditor]);

  useEffect(() => {
    if (selectedUser) {
      setSelectedRole(selectedUser.roles);
      setSelectedOrgIds(getUserOrgIds(selectedUser));
      const raw = selectedUser.page_scopes;
      const filteredScopes =
        raw?.filter((scope) => ASSIGNABLE_PAGE_SCOPES.includes(scope)) ?? [];
      setSelectedPageScopes(
        filteredScopes.length > 0 ? filteredScopes : ["CASHFLOW"],
      );
    }
  }, [selectedUser]);

  const handleUpdate = () => {
    if (!selectedUser) return;
    const normalizedCurrentScopes = [
      ...new Set(
        (selectedUser.page_scopes ?? [])
          .filter((scope) => ASSIGNABLE_PAGE_SCOPES.includes(scope))
          .sort(),
      ),
    ];
    const baselineScopes =
      normalizedCurrentScopes.length > 0
        ? normalizedCurrentScopes
        : ["CASHFLOW"];
    const nextScopes = [...new Set(selectedPageScopes)].sort();
    const changes: PendingChange[] = [];
    if (selectedUser.roles !== selectedRole) {
      changes.push({
        field: "Role",
        from: selectedUser.roles,
        to: selectedRole,
      });
    }
    const currentOrgIds = getUserOrgIds(selectedUser).slice().sort();
    const nextOrgIds = [...new Set(selectedOrgIds)].sort();
    if (currentOrgIds.join(", ") !== nextOrgIds.join(", ")) {
      changes.push({
        field: "Linked Organizations",
        from: resolveOrgNames(currentOrgIds),
        to: resolveOrgNames(nextOrgIds),
      });
    }
    if (baselineScopes.join(", ") !== nextScopes.join(", ")) {
      changes.push({
        field: "Page Scopes",
        from: baselineScopes.join(", "),
        to: nextScopes.join(", "),
      });
    }

    if (changes.length === 0) return;
    setPendingChanges(changes);
  };

  const handleConfirmUpdate = () => {
    if (!selectedUser || !pendingChanges) return;
    setPendingChanges(null);
    onUpdateUser(
      selectedUser.id,
      selectedRole,
      selectedOrgIds,
      selectedPageScopes,
    );
  };

  const toggleScope = (scope: PageScope) => {
    setSelectedPageScopes((prev) => {
      if (prev.includes(scope)) {
        const next = prev.filter((s) => s !== scope);
        return next.length > 0 ? next : ["CASHFLOW"];
      }
      return [...prev, scope];
    });
  };

  const formatAdminDateTime = (
    value: string | null | undefined,
    emptyLabel: string,
  ) => {
    if (!value) return emptyLabel;
    if (Number.isNaN(parseApiDateTime(value).getTime())) return INVALID_FALLBACK;
    return formatAppDateTime(value);
  };

  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Users</h2>
        <p className="text-sm text-slate-600">
          Inspect roles, organization links, and UI page scopes.
        </p>
      </div>

      <div className="max-w-md">
        <label htmlFor="admin-user-search" className="sr-only">
          Search users
        </label>
        <input
          id="admin-user-search"
          type="search"
          value={userSearch}
          onChange={(e) => setUserSearch(e.target.value)}
          placeholder="Search by name, email, ID, role, organization…"
          autoComplete="off"
          className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {filteredUsers.map((user) => {
          const isSelected = selectedUserId === user.id;
          const roleTone =
            user.roles === "ADMIN"
              ? "bg-purple-100 text-purple-700"
              : user.roles === "TRIAL"
                ? "bg-amber-100 text-amber-700"
                : "bg-indigo-100 text-indigo-700";

          return (
            <button
              type="button"
              key={user.id}
              onClick={() => setSelectedUserId(user.id)}
              className={`rounded-2xl border p-5 text-left transition ${
                isSelected
                  ? "border-indigo-300 bg-indigo-50 shadow-sm"
                  : "border-slate-200 bg-white hover:border-indigo-200 hover:shadow-sm"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-base font-semibold leading-snug text-slate-900">
                    {user.username}
                  </p>
                  <p className="mt-0.5 text-sm leading-snug text-slate-600">
                    {user.email}
                  </p>
                </div>
                <span
                  className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${roleTone}`}
                >
                  {user.roles}
                </span>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Organization
                  </p>
                  <p className="mt-0.5 font-medium leading-snug text-slate-900">
                    {resolveOrgNames(getUserOrgIds(user))}
                  </p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Uploaded file
                  </p>
                  <p className="mt-0.5 font-medium text-slate-900">
                    {user.has_uploaded_file ? "Yes" : "No"}
                  </p>
                </div>
              </div>

              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Last login
                  </p>
                  <p className="mt-0.5 font-medium leading-snug text-slate-900">
                    {formatAdminDateTime(user.last_logged_in, "Never")}
                  </p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Account created
                  </p>
                  <p className="mt-0.5 font-medium leading-snug text-slate-900">
                    {formatAdminDateTime(user.created_at, "Unknown")}
                  </p>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                {(user.page_scopes ?? ["CASHFLOW"]).slice(0, 4).map((scope) => (
                  <span
                    key={`${user.id}-${scope}`}
                    className="rounded-md bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200"
                  >
                    {scope}
                  </span>
                ))}
              </div>
            </button>
          );
        })}
        {users.length === 0 ? (
          <div className="col-span-full rounded-xl border border-slate-200 bg-slate-50 p-4 text-center text-sm text-slate-500">
            No users found.
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="col-span-full rounded-xl border border-slate-200 bg-slate-50 p-4 text-center text-sm text-slate-500">
            No users match your search.
          </div>
        ) : null}
      </div>

      {selectedUser && (
        <>
          <div
            role="presentation"
            className="fixed inset-0 z-110 bg-slate-900/40"
            onClick={closeUserEditor}
          />
          <aside
            className="fixed inset-y-0 right-0 z-120 flex w-full max-w-lg flex-col border-l border-slate-200 bg-white shadow-2xl"
            aria-labelledby="user-edit-title"
            aria-modal="true"
            role="dialog"
          >
            <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
              <div className="min-w-0">
                <p
                  id="user-edit-title"
                  className="truncate text-lg font-semibold text-slate-900"
                >
                  Update {selectedUser.username}
                </p>
                <p className="mt-0.5 truncate text-sm text-slate-500">
                  {selectedUser.email}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  Account created:{" "}
                  <span className="font-medium text-slate-700">
                    {formatAdminDateTime(selectedUser.created_at, "Unknown")}
                  </span>
                </p>
              </div>
              <button
                type="button"
                onClick={closeUserEditor}
                className="shrink-0 rounded-lg border border-slate-200 p-2 text-slate-600 transition hover:bg-slate-50"
                aria-label="Close"
              >
                <IconX size={20} stroke={1.75} />
              </button>
            </div>

            <div className="flex min-h-0 flex-1 flex-col">
              <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-5 py-4">
                <div className="flex flex-wrap gap-3 text-sm text-slate-700">
                  <label className="flex items-center gap-2">
                    Role
                    <select
                      className="rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                      value={selectedRole}
                      onChange={(e) =>
                        setSelectedRole(e.target.value as UserRole)
                      }
                    >
                      {roleOptions.map((role) => (
                        <option key={role} value={role}>
                          {role}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <div className="flex flex-col gap-2">
                  <p className="text-sm font-medium text-slate-800">
                    Page scopes
                  </p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {ASSIGNABLE_PAGE_SCOPES.map((scope) => {
                      const scopeMeta = PAGE_SCOPE_META[scope];
                      const scopeSelected = selectedPageScopes.includes(scope);

                      return (
                        <button
                          type="button"
                          key={scope}
                          onClick={() => toggleScope(scope)}
                          className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition ${
                            scopeSelected
                              ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                              : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                          }`}
                        >
                          <scopeMeta.Icon
                            size={16}
                            className={
                              scopeSelected
                                ? "text-indigo-600"
                                : "text-slate-500"
                            }
                          />
                          <span className="font-medium">{scopeMeta.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-slate-500">
                    At least one scope is required; clearing all restores
                    Cashflow-only.
                  </p>
                </div>

                <UserOrgLinkPicker
                  key={selectedUser.id}
                  organizations={organizations}
                  selectedOrgIds={selectedOrgIds}
                  onChange={setSelectedOrgIds}
                  disabled={isSubmitting}
                />
              </div>

              <div className="flex flex-wrap gap-2 border-t border-slate-200 px-5 py-4">
                <button
                  type="button"
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
                  onClick={handleUpdate}
                  disabled={isSubmitting}
                >
                  Save user
                </button>
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  onClick={closeUserEditor}
                >
                  Cancel
                </button>
              </div>
            </div>
          </aside>
        </>
      )}
      <ChangeConfirmationModal
        isOpen={pendingChanges !== null}
        title="Confirm user updates"
        description="Review the pending user changes before saving."
        changes={pendingChanges ?? []}
        onCancel={() => setPendingChanges(null)}
        onConfirm={handleConfirmUpdate}
        confirmLabel="Save user"
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

const Admin = () => {
  const navigate = useNavigate();
  const { data: organizations = [], isLoading: isOrgLoading } =
    useAdminOrganizationsQuery();
  const { data: users = [], isLoading: isUserLoading } = useAdminUsersQuery();
  const createOrganization = useCreateOrganizationMutation();
  const updateOrganization = useUpdateOrganizationMutation();
  const updateUser = useUpdateUserMutation();
  const zohoAuthorize = useZohoAuthorizeUrlMutation();
  const initZohoToken = useInitZohoTokenMutation();

  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
  const [zohoAuthorizationCode, setZohoAuthorizationCode] = useState("");
  const [zohoEndpointToken, setZohoEndpointToken] = useState("");
  const [activeTab, setActiveTab] = useState<AdminTab>("USERS");

  const selectedOrg = useMemo(
    () => organizations.find((org) => org.id === selectedOrgId) || null,
    [organizations, selectedOrgId],
  );

  const handleSubmitOrg = (data: OrgFormState, orgId?: number) => {
    const payload = {
      name: data.name,
      support_email: data.support_email,
      official_outgoing_email:
        data.official_outgoing_email.trim().length > 0
          ? data.official_outgoing_email
          : null,
      use_official_outgoing_email: data.use_official_outgoing_email,
      priority_api_url:
        data.priority_api_url.trim().length > 0 ? data.priority_api_url : null,
    };

    if (orgId) {
      updateOrganization.mutate({ id: orgId, ...payload });
    } else {
      createOrganization.mutate(payload);
    }
  };

  const handleUpdateUser = (
    userId: number,
    role: UserRole,
    organizationIds: number[],
    pageScopes: PageScope[],
  ) => {
    const sanitizedScopes = pageScopes.filter((scope) =>
      ASSIGNABLE_PAGE_SCOPES.includes(scope),
    );
    updateUser.mutate({
      userId,
      roles: role,
      budy_organization_ids: organizationIds,
      page_scopes: sanitizedScopes.length > 0 ? sanitizedScopes : ["CASHFLOW"],
    });
  };

  const handleStartZohoOauth = async () => {
    try {
      const data = await zohoAuthorize.mutateAsync();
      window.open(data.authorization_url, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Failed to fetch Zoho OAuth authorization URL", error);
    }
  };

  const handleInitZohoToken = async () => {
    try {
      const data = await initZohoToken.mutateAsync({
        authorization_code: zohoAuthorizationCode.trim(),
      });
      setZohoEndpointToken(data.endpoint_access_token);
    } catch (error) {
      console.error("Failed to initialize Zoho endpoint token", error);
    }
  };

  if (isOrgLoading || isUserLoading) {
    return (
      <div className="flex h-full items-center justify-center text-gray-600">
        Loading admin data...
      </div>
    );
  }

  return (
    <div className="min-h-full w-full bg-slate-50">
      <div className="mx-auto flex w-full max-w-none flex-col gap-6 px-4 py-6 sm:px-6 md:px-8 lg:px-10 xl:px-12 2xl:px-14">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Admin</h1>
            <p className="text-sm text-slate-600">
              Manage organizations, users, draft inspection, and integrations.
            </p>
          </div>
          <AdminScope>
            <button
              onClick={() => navigate("/admin/draft-validation")}
              className="rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-medium text-indigo-700 transition hover:bg-indigo-100"
            >
              Draft Validation
            </button>
          </AdminScope>
        </div>

        <TrialScope>
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Trial role detected. Mutations may be limited by backend policy.
          </div>
        </TrialScope>

        <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
          <div className="flex flex-wrap gap-2">
            {ADMIN_TABS.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`rounded-lg border px-3 py-2 text-sm font-medium transition ${
                    isActive
                      ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {activeTab === "ORGANIZATIONS" && (
          <OrganizationsPanel
            organizations={organizations}
            onSubmit={handleSubmitOrg}
            onSelect={setSelectedOrgId}
            selectedOrg={selectedOrg}
            isSubmitting={
              updateOrganization.isPending || createOrganization.isPending
            }
          />
        )}

        {activeTab === "USERS" && (
          <UsersPanel
            users={users}
            organizations={organizations}
            onUpdateUser={handleUpdateUser}
            isSubmitting={updateUser.isPending}
          />
        )}

        {activeTab === "STATISTICS" && (
          <AdminStatisticsPanel users={users} organizations={organizations} />
        )}

        {activeTab === "ZOHO" && (
          <AdminScope>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900">
                Zoho OAuth Initialization
              </h2>
              <p className="mt-1 text-sm text-slate-600">
                First-time setup flow: start Zoho OAuth, then paste the returned
                authorization code to generate the endpoint access token.
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handleStartZohoOauth}
                  disabled={zohoAuthorize.isPending}
                  className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {zohoAuthorize.isPending
                    ? "Opening Zoho..."
                    : "Start Zoho OAuth"}
                </button>
                <input
                  type="text"
                  value={zohoAuthorizationCode}
                  onChange={(e) => setZohoAuthorizationCode(e.target.value)}
                  placeholder="Paste authorization code from /zoho/oauth/callback"
                  className="min-w-[280px] flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                />
                <button
                  type="button"
                  onClick={handleInitZohoToken}
                  disabled={
                    initZohoToken.isPending ||
                    zohoAuthorizationCode.trim().length === 0
                  }
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
                >
                  {initZohoToken.isPending
                    ? "Initializing..."
                    : "Generate Endpoint Token"}
                </button>
              </div>
              {zohoEndpointToken && (
                <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3">
                  <p className="text-xs font-medium text-amber-800">
                    Save this endpoint access token now (shown once in UI):
                  </p>
                  <code className="mt-1 block break-all text-xs text-amber-900">
                    {zohoEndpointToken}
                  </code>
                </div>
              )}
            </div>
          </AdminScope>
        )}
      </div>
    </div>
  );
};

export default Admin;
